import { useId } from "react"

import type { AgentCharacterId, AgentSignature } from "../utils/agent-character-utils"
import type { AgentPresence, WorkingActivityKind } from "../utils/agent-presence-utils"

import styles from "./agent-character.module.css"
import { agentCharacterSignatures } from "../utils/agent-character-utils"

type Props = {
  character: AgentCharacterId
  state: AgentPresence
  className?: string
  still?: boolean
  paused?: boolean
  celebrate?: boolean
  activity?: WorkingActivityKind | null
  /** A pending approval needs an explicit attention signal, unlike an unread reply. */
  attention?: boolean
  compact?: boolean
}

type Expression = "idle" | "thinking" | "working" | "waiting" | "attention" | "done" | "blocked"

const BROW_PATHS: Partial<Record<Expression, string[]>> = {
  waiting: ["M-66-28Q-43-41-20-29", "M20-29Q43-41 66-28"],
  attention: ["M-65-39Q-44-53-24-40", "M24-40Q44-53 65-39"],
  blocked: ["M-65-20Q-44-32-23-20", "M23-20Q44-32 65-20"],
}

function expressionFor(state: AgentPresence, attention: boolean): Expression {
  if (state === "waiting" && attention) return "attention"
  if (state === "thinking" || state === "working" || state === "waiting" || state === "done")
    return state
  if (state === "blocked") return "blocked"
  // Loading, reconnecting and cancellation are transport states, not agent work or failure.
  return "idle"
}

function Eyes({ expression }: { expression: Expression }) {
  const brows = BROW_PATHS[expression]?.map((path) => <path key={path} d={path} />)

  let paths: { d: string; width: number }[]
  switch (expression) {
    case "thinking":
      paths = [
        { d: "M-45-18V15", width: 17 },
        { d: "M28 8Q43-11 58 2", width: 13 },
      ]
      break
    case "working":
      paths = [
        { d: "M-61 0-27 7", width: 12 },
        { d: "M27 7 61 0", width: 12 },
      ]
      break
    case "waiting":
      paths = [
        { d: "M-27 0a16 16 0 1 1-32 0a16 16 0 1 1 32 0", width: 7 },
        { d: "M59 0a16 16 0 1 1-32 0a16 16 0 1 1 32 0", width: 7 },
      ]
      break
    case "attention":
      paths = [
        { d: "M-44-19V19", width: 22 },
        { d: "M44-19V19", width: 22 },
      ]
      break
    case "done":
      paths = [
        { d: "M-65 11C-60-20-29-20-23 11", width: 13.5 },
        { d: "M23 11C29-20 60-20 65 11", width: 13.5 },
      ]
      break
    case "blocked":
      paths = [
        { d: "M-62 4Q-44 18-26 4", width: 11 },
        { d: "M26 4Q44 18 62 4", width: 11 },
      ]
      break
    default:
      paths = [
        { d: "M-43-17V17", width: 18 },
        { d: "M43-17V17", width: 18 },
      ]
  }

  return (
    <g className={styles.eyes} fill="none" strokeLinecap="round" strokeLinejoin="round">
      <g className={styles.eyeGlow}>
        {paths.map(({ d, width }) => (
          <path key={d} d={d} strokeWidth={width + 4} />
        ))}
      </g>
      <g className={styles.eyeCrisp}>
        {paths.map(({ d, width }) => (
          <path key={d} d={d} strokeWidth={width} />
        ))}
      </g>
      {brows ? (
        <g className={styles.brows} strokeWidth="3.8">
          {brows}
        </g>
      ) : null}
      {expression === "waiting" ? (
        <g className={styles.pupils} stroke="none">
          <circle cx="-43" cy="1" r="4.8" />
          <circle cx="43" cy="1" r="4.8" />
        </g>
      ) : null}
    </g>
  )
}

function SignalIcon({ expression }: { expression: Expression }) {
  switch (expression) {
    case "thinking":
      return (
        <g fill="currentColor" stroke="none">
          <circle cx="-13" r="3.8" />
          <circle r="3.8" />
          <circle cx="13" r="3.8" />
        </g>
      )
    case "working":
      return (
        <g className={styles.gear}>
          <path
            d="M-5-20h10l1 7 5 3 6-3 5 9-6 4v6l6 4-5 9-6-3-5 3-1 7H-5l-1-7-5-3-6 3-5-9 6-4V0l-6-4 5-9 6 3 5-3Z"
            transform="translate(0 -3) scale(.8)"
          />
          <circle r="6" />
        </g>
      )
    case "waiting":
      return (
        <>
          <path d="M-12-10c1-14 27-14 27 0 0 8-14 9-14 17" />
          <circle cx="1" cy="18" fill="currentColor" r="2.6" stroke="none" />
        </>
      )
    case "attention":
      return (
        <>
          <path d="M0-18V4" strokeWidth="6" />
          <circle cy="17" fill="currentColor" r="3.4" stroke="none" />
        </>
      )
    case "done":
      return <path d="m-18 0 12 12L19-14" />
    case "blocked":
      return <path d="m-13-13 26 26M13-13-13 13" />
    default:
      return <path d="M0-17V-2M-11-11a17 17 0 1 0 22 0" />
  }
}

function Signature({ shape }: { shape: AgentSignature }) {
  switch (shape) {
    case "diamond":
      return <rect height="44" rx="5" transform="rotate(45)" width="44" x="-22" y="-22" />
    case "petal":
      return (
        <path d="M0-31C18-26 29-12 29 4C29 20 14 31 0 31C-14 31-29 20-29 4C-29-12-18-26 0-31Z" />
      )
    case "star":
      return <path d="m0-32 8 21 22 3-16 15 5 23L0 18l-19 12 5-23-16-15 22-3Z" />
    case "sun":
      return (
        <>
          <circle r="21" />
          <path
            d="M0-32v-5M0 32v5M-32 0h-5M32 0h5M-23-23l-4-4M23 23l4 4M23-23l4-4M-23 23l-4 4"
            fill="none"
            strokeWidth="4"
          />
        </>
      )
    case "hexagon":
      return <path d="M-17-29h34l17 29-17 29h-34L-34 0Z" />
    case "capsule":
      return <rect height="43" rx="21.5" transform="rotate(-35)" width="62" x="-31" y="-21.5" />
    case "crescent":
      return <path d="M17-27A31 31 0 1 0 18 27A24 24 0 0 1 17-27Z" />
    default:
      return <circle r="31" />
  }
}

/** Constellation Core portrait, adapted from the owner's moon-orb prototype. */
export function AgentCharacter({
  character,
  state,
  className = "",
  still = false,
  paused = false,
  celebrate = false,
  activity = null,
  attention = false,
  compact = false,
}: Props) {
  const expression = expressionFor(state, attention)
  const signature = agentCharacterSignatures[character]
  const id = useId().replace(/:/g, "")
  const gradient = (name: string) => `url(#${id}-${name})`
  const satellite = ({
    x,
    y,
    r,
    kind,
    key,
  }: {
    x: number
    y: number
    r: number
    kind: "graphite" | "ivory" | "steel" | "identity"
    key: string
  }) => (
    <g key={key} transform={`translate(${x} ${y})`}>
      <circle fill={gradient(kind)} r={r} stroke={gradient("light-rim")} strokeWidth="1.2" />
      <ellipse
        cx={-r * 0.26}
        cy={-r * 0.32}
        fill={gradient("sheen")}
        opacity=".35"
        rx={r * 0.7}
        ry={r * 0.56}
        transform="rotate(-25)"
      />
    </g>
  )
  const signal = (
    <g transform={compact ? "translate(340 145) scale(1.35)" : "translate(416 126)"}>
      <g className={styles.signalFace}>
        <circle fill={gradient("signal")} r="31" stroke="#34393b" strokeWidth="1.2" />
        <path
          d="M-25-9A27 27 0 0 1 8-25"
          fill="none"
          opacity=".75"
          stroke="#fff"
          strokeLinecap="round"
          strokeWidth="1.5"
        />
        <g
          color="#34393b"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="4.4"
          transform="scale(.79)"
        >
          <SignalIcon expression={expression} />
        </g>
      </g>
    </g>
  )

  return (
    <svg
      aria-hidden="true"
      className={`${styles.character} ${className}`}
      data-activity={state === "working" && activity ? activity : undefined}
      data-celebrate={celebrate}
      data-character={character}
      data-compact={compact}
      data-expression={expression}
      data-paused={paused}
      data-state={state}
      data-still={still}
      fill="none"
      viewBox={compact ? "105 100 310 310" : "0 0 500 500"}
    >
      <defs>
        <radialGradient cx="28%" cy="17%" data-part="core-gradient" id={`${id}-core`} r="89%">
          <stop className={styles.coreHighlight} />
          <stop className={styles.coreUpper} offset=".19" />
          <stop className={styles.coreMiddle} offset=".5" />
          <stop className={styles.coreShadow} offset=".83" />
          <stop className={styles.coreEdge} offset="1" />
        </radialGradient>
        <radialGradient cx="26%" cy="18%" id={`${id}-graphite`} r="88%">
          <stop stopColor="#758084" />
          <stop offset=".22" stopColor="#435055" />
          <stop offset=".61" stopColor="#142128" />
          <stop offset=".87" stopColor="#09141c" />
          <stop offset="1" stopColor="#35515c" />
        </radialGradient>
        <radialGradient cx="27%" cy="18%" id={`${id}-ivory`} r="87%">
          <stop stopColor="#fffefb" />
          <stop offset=".24" stopColor="#faf7ef" />
          <stop offset=".57" stopColor="#dedbd1" />
          <stop offset=".83" stopColor="#a9b3b0" />
          <stop offset="1" stopColor="#bcdce0" />
        </radialGradient>
        <radialGradient cx="28%" cy="18%" id={`${id}-steel`} r="91%">
          <stop stopColor="#d8e6e8" />
          <stop offset=".27" stopColor="#a1bbc3" />
          <stop offset=".64" stopColor="#62838f" />
          <stop offset=".86" stopColor="#36535f" />
          <stop offset="1" stopColor="#9acddb" />
        </radialGradient>
        <radialGradient id={`${id}-sheen`}>
          <stop stopColor="#fff" stopOpacity=".52" />
          <stop offset=".48" stopColor="#fff" stopOpacity=".13" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-floor`}>
          <stop stopColor="#152228" stopOpacity=".26" />
          <stop offset="1" stopColor="#152228" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-rim`} x1="12%" x2="82%" y1="3%" y2="98%">
          <stop stopColor="#f0feff" />
          <stop offset=".24" stopColor="#b2cdd5" />
          <stop offset=".52" stopColor="#344c5b" />
          <stop className={styles.identityRim} offset=".81" />
          <stop offset="1" stopColor="#e7fcff" />
        </linearGradient>
        <radialGradient cx="27%" cy="17%" id={`${id}-identity`} r="91%">
          <stop className={styles.identityLight} />
          <stop className={styles.identityBase} offset=".48" />
          <stop className={styles.identityShade} offset="1" />
        </radialGradient>
        <linearGradient id={`${id}-light-rim`} x1="10%" x2="90%" y2="80%">
          <stop stopColor="#d6f8ff" stopOpacity=".8" />
          <stop offset=".4" stopColor="#a6e7ff" stopOpacity=".1" />
          <stop offset="1" stopColor="#a6e7ff" stopOpacity=".48" />
        </linearGradient>
        <radialGradient cx="31%" cy="22%" id={`${id}-signal`} r="91%">
          <stop className={styles.signalLight} />
          <stop className={styles.signalBase} offset=".52" />
          <stop className={styles.signalShade} offset="1" />
        </radialGradient>
      </defs>
      <ellipse cx="250" cy="459" fill={gradient("floor")} rx="136" ry="19" />
      <g className={styles.world}>
        <g className={styles.guides} stroke="#83b3c3">
          <ellipse cx="250" cy="246" rx="187" ry="142" strokeOpacity=".23" />
          <ellipse cx="250" cy="246" rx="206" ry="169" strokeDasharray="2 10" strokeOpacity=".28" />
        </g>
        <g className={styles.satelliteOrbit}>
          <g
            className={styles.signature}
            data-part="identity-signature"
            data-shape={signature}
            fill={gradient("identity")}
            stroke="#34393b"
            strokeLinejoin="round"
            strokeWidth="1.5"
            transform="translate(145 139)"
          >
            <Signature shape={signature} />
            <path
              d="M-17-7Q-9-22 5-23"
              fill="none"
              opacity=".5"
              stroke="#fff"
              strokeLinecap="round"
              strokeWidth="2"
            />
          </g>
          {satellite({ x: 67, y: 216, r: 32, kind: "ivory", key: "two" })}
          {satellite({ x: 163, y: 87, r: 29, kind: "steel", key: "three" })}
          {satellite({ x: 381, y: 333, r: 36, kind: "identity", key: "four" })}
          {satellite({ x: 209, y: 395, r: 29, kind: "graphite", key: "five" })}
          {satellite({ x: 436, y: 222, r: 30, kind: "ivory", key: "six" })}
          {satellite({ x: 345, y: 105, r: 24, kind: "identity", key: "seven" })}
        </g>
        <g className={styles.signalOrbit} data-part="signal-orb">
          {signal}
        </g>
        <g className={styles.core} data-part="core">
          <circle cx="250" cy="246" fill={gradient("rim")} r="116" />
          <circle cx="250" cy="246" fill={gradient("core")} r="113.5" />
          <path
            className={styles.identityGlint}
            d="M151 280A108 108 0 0 1 223 143"
            strokeLinecap="round"
            strokeWidth="5.5"
          />
          <ellipse
            cx="210"
            cy="193"
            fill={gradient("sheen")}
            opacity=".34"
            rx="68"
            ry="55"
            transform="rotate(-28 210 193)"
          />
          <g className={styles.gaze} transform="translate(250 249)">
            <Eyes expression={expression} />
          </g>
        </g>
      </g>
    </svg>
  )
}
